import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TwoFactorAuth = () => {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/2fa/status`, {
        withCredentials: true
      });
      setStatus(response.data);
    } catch (error) {
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/2fa/setup`, {}, {
        withCredentials: true
      });
      setSetupData(response.data);
      setShowSetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Ingresá el código de 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/2fa/verify`, { code: verifyCode }, {
        withCredentials: true
      });
      toast.success('2FA activado correctamente');
      setShowSetup(false);
      setSetupData(null);
      setVerifyCode('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Ingresá tu código actual de 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/2fa/disable`, { code: verifyCode }, {
        withCredentials: true
      });
      toast.success('2FA desactivado');
      setVerifyCode('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  if (!status?.available) return null;

  return (
    <Card className="bg-white/50 border-[#E5E5E5]" data-testid="2fa-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0A0A0A]">
          {status.enabled ? <ShieldCheck className="w-6 h-6 text-green-400" /> : <Shield className="w-6 h-6 text-[#737373]" />}
          Autenticación de Dos Factores (2FA)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.enabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-green-400" />
              <p className="text-green-300 text-sm">2FA está activo en tu cuenta</p>
            </div>
            <div className="border-t border-[#E5E5E5] pt-4">
              <Label className="text-[#0A0A0A]">Desactivar 2FA</Label>
              <p className="text-xs text-[#737373] mb-2">Ingresá tu código actual para desactivar</p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-white border-[#E5E5E5] text-[#0A0A0A] font-mono text-center text-xl tracking-widest"
                  data-testid="2fa-disable-code-input"
                />
                <Button
                  onClick={disable2FA}
                  disabled={loading}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="disable-2fa-button"
                >
                  <ShieldOff className="w-4 h-4 mr-1" />
                  Desactivar
                </Button>
              </div>
            </div>
          </div>
        ) : !showSetup ? (
          <div className="space-y-4">
            <p className="text-[#525252] text-sm">
              Protegé tu cuenta con un código adicional desde Google Authenticator, Authy, o similar.
            </p>
            <Button
              onClick={startSetup}
              disabled={loading}
              className="bg-primary hover:bg-primary/90 text-white"
              data-testid="enable-2fa-button"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              {loading ? 'Generando...' : 'Activar 2FA'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white/50 p-4 rounded-lg space-y-3">
              <h4 className="font-semibold text-[#0A0A0A]">Paso 1: Escaneá el QR</h4>
              <p className="text-xs text-[#737373]">Abrí Google Authenticator o Authy y escaneá:</p>
              {setupData?.qr_code && (
                <div className="flex justify-center bg-white p-4 rounded-lg">
                  <img src={setupData.qr_code} alt="QR 2FA" className="w-48 h-48" data-testid="2fa-qr-image" />
                </div>
              )}
              <div className="text-xs text-[#737373]">
                <p>¿No podés escanear? Ingresá manualmente:</p>
                <code className="block mt-1 p-2 bg-white rounded text-primary font-mono break-all" data-testid="2fa-secret">
                  {setupData?.secret}
                </code>
              </div>
            </div>

            <div className="bg-white/50 p-4 rounded-lg space-y-3">
              <h4 className="font-semibold text-[#0A0A0A]">Paso 2: Ingresá el código</h4>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="bg-white border-[#E5E5E5] text-[#0A0A0A] font-mono text-center text-2xl tracking-widest"
                data-testid="2fa-verify-code-input"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => { setShowSetup(false); setSetupData(null); setVerifyCode(''); }}
                  variant="outline"
                  className="flex-1 border-[#E5E5E5]"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={verifyAndEnable}
                  disabled={loading || verifyCode.length !== 6}
                  className="flex-1 bg-primary hover:bg-primary/90"
                  data-testid="verify-2fa-button"
                >
                  {loading ? 'Verificando...' : 'Activar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorAuth;
